import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import Image from 'next/image'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface RevealImageProps {
    src: string
    alt: string
    className?: string
    imageClassName?: string
    priority?: boolean
    sizes?: string
    /** Direction the clip-path opens from. */
    from?: 'bottom' | 'top' | 'left' | 'right'
    delay?: number
    duration?: number
    /** Scroll-linked drift, in pixels, applied across the viewport pass. */
    parallax?: number
    /** Extra zoom held behind the mask and released as it opens. */
    scale?: number
    trigger?: boolean
}

const CLIP_FROM: Record<string, string> = {
    bottom: 'inset(100% 0% 0% 0%)',
    top: 'inset(0% 0% 100% 0%)',
    left: 'inset(0% 100% 0% 0%)',
    right: 'inset(0% 0% 0% 100%)'
}

/**
 * The core image gesture of the site: a clip-path wipe that uncovers the
 * frame while the photograph itself settles back from a slight zoom, with an
 * optional scroll-linked drift once it has landed.
 */
export const RevealImage: FC<RevealImageProps> = ({
    src,
    alt,
    className,
    imageClassName,
    priority = false,
    sizes = '(max-width: 768px) 100vw, 50vw',
    from = 'bottom',
    delay = 0,
    duration = 1.5,
    parallax = 0,
    scale = 1.28,
    trigger = true
}): ReactNode => {
    const root = useRef<HTMLDivElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            const frame = element.querySelector<HTMLElement>('[data-frame]')
            const picture = element.querySelector<HTMLElement>('[data-picture]')

            if (!frame || !picture) {
                return
            }

            gsap.set(element, { visibility: 'visible' })

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            gsap.set(frame, { clipPath: CLIP_FROM[from], willChange: 'clip-path' })
            gsap.set(picture, { scale, transformOrigin: 'center center' })

            const timeline = gsap.timeline({
                defaults: { ease: 'expo.out' },
                scrollTrigger: trigger
                    ? {
                        trigger: element,
                        start: 'top 88%',
                        once: true
                    }
                    : undefined
            })

            timeline
                .to(frame, { clipPath: 'inset(0% 0% 0% 0%)', duration, delay }, 0)
                .to(picture, { scale: 1, duration: duration * 1.12, delay }, 0)

            if (parallax !== 0) {
                gsap.fromTo(
                    picture,
                    { yPercent: -parallax / 2 },
                    {
                        yPercent: parallax / 2,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: element,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true
                        }
                    }
                )
            }
        }, element)

        return () => context.revert()
    }, [from, delay, duration, parallax, scale, trigger])

    return (
        <div
            ref={root}
            data-reveal
            className={cn('relative overflow-hidden', className)}
        >
            <div data-frame className='relative h-full w-full overflow-hidden'>
                <div data-picture className='relative h-full w-full will-change-transform'>
                    <Image
                        src={src}
                        alt={alt}
                        fill
                        priority={priority}
                        sizes={sizes}
                        className={cn('object-cover', imageClassName)}
                    />
                </div>
            </div>
        </div>
    )
}
